/**
 * Adapter de autosave do rascunho da Calculadora Completa (Fase 2, §8.1/§8.2).
 *
 * Hoje: só localStorage (proteção contra crash/fechamento da aba, mesmo navegador).
 * Server-side (troca de máquina) é PB-03/PB-04 — quando o back entregar, trocar
 * a implementação de `autosaveAdapter` mantendo a interface `AutosaveAdapter`.
 *
 * Chave: `juscalc:completa:rascunho:${userId}`. Sem userId → não salva.
 */

export interface RascunhoCalculo {
  versao: 1;
  /** ISO 8601 */
  salvoEm: string;
  step: number;
  qtdParcelas: number;
  values: unknown;
}

export interface AutosaveAdapter {
  /** @returns false se não conseguiu persistir (quota, storage indisponível). */
  salvar(userId: string | number, rascunho: RascunhoCalculo): boolean;
  /** @returns null se não há rascunho, se está corrompido ou se expirou (TTL). */
  carregar(userId: string | number): RascunhoCalculo | null;
  limpar(userId: string | number): void;
  /** Remove rascunhos de todos os usuários (logout). */
  limparTodos(): void;
}

export const PREFIXO_CHAVE = 'juscalc:completa:rascunho:';
export const chaveRascunho = (userId: string | number) =>
  `${PREFIXO_CHAVE}${userId}`;

/** §8.2: rascunho não-finalizado nas últimas 48h. */
export const RASCUNHO_TTL_MS = 48 * 60 * 60 * 1000;

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** userId ausente/vazio nunca vira chave: evita o rascunho global `...rascunho:undefined`. */
const userIdInvalido = (userId: string | number | undefined | null) =>
  userId === undefined || userId === null || userId === '';

const expirado = (r: RascunhoCalculo, agora = Date.now()) => {
  const t = new Date(r.salvoEm).getTime();
  // `salvoEm` corrompido dá NaN; sem esta guarda `NaN > TTL` é false e o
  // rascunho inválido nunca expiraria.
  if (!Number.isFinite(t)) return true;
  return agora - t > RASCUNHO_TTL_MS;
};

export const localStorageAutosaveAdapter: AutosaveAdapter = {
  salvar(userId, rascunho) {
    const s = storage();
    if (!s || userIdInvalido(userId)) {
      return false;
    }
    try {
      s.setItem(chaveRascunho(userId), JSON.stringify(rascunho));
      return true;
    } catch {
      // QuotaExceededError / SecurityError — nunca quebra a UI.
      return false;
    }
  },

  carregar(userId) {
    const s = storage();
    if (!s || userIdInvalido(userId)) {
      return null;
    }
    try {
      const raw = s.getItem(chaveRascunho(userId));
      if (!raw) return null;
      const r = JSON.parse(raw) as RascunhoCalculo;
      if (
        !r ||
        r.versao !== 1 ||
        typeof r.salvoEm !== 'string' ||
        typeof r.step !== 'number' ||
        !r.values
      ) {
        return null;
      }
      if (expirado(r)) {
        s.removeItem(chaveRascunho(userId));
        return null;
      }
      return r;
    } catch {
      return null;
    }
  },

  limpar(userId) {
    const s = storage();
    if (!s || userIdInvalido(userId)) return;
    try {
      s.removeItem(chaveRascunho(userId));
    } catch {
      // SecurityError — limpar é best-effort; falhar aqui não pode quebrar o logout.
    }
  },

  limparTodos() {
    const s = storage();
    if (!s) return;
    try {
      const chaves: string[] = [];
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i);
        if (k && k.startsWith(PREFIXO_CHAVE)) chaves.push(k);
      }
      chaves.forEach((k) => s.removeItem(k));
    } catch {
      // SecurityError / storage indisponível — logout não pode falhar por causa do rascunho.
    }
  },
};

/** Ponto único de troca para o back (PB-03/PB-04). */
export const autosaveAdapter: AutosaveAdapter = localStorageAutosaveAdapter;

/* ------------------------------------------------------------------------- *
 * BB1 — duas fontes de rascunho (local + servidor).
 *
 * Decisão do PM (2026-09-09): não existe escolha entre versões. Vence o
 * rascunho com `salvoEm` mais recente; em caso de empate vence o servidor
 * (fonte compartilhada entre dispositivos). O banner informa a origem.
 * ------------------------------------------------------------------------- */

export type OrigemRascunho = 'local' | 'servidor';

/** Fonte assíncrona de rascunho. `salvar`/`limpar` são opcionais (fonte só-leitura). */
export interface FonteRascunho {
  origem: OrigemRascunho;
  carregar(userId: string | number): Promise<RascunhoCalculo | null>;
  salvar?(userId: string | number, rascunho: RascunhoCalculo): Promise<boolean>;
  limpar?(userId: string | number): Promise<void>;
}

/** Envolve o adapter síncrono de localStorage na interface assíncrona. */
export const fonteLocal: FonteRascunho = {
  origem: 'local',
  carregar: async (userId) => localStorageAutosaveAdapter.carregar(userId),
  salvar: async (userId, rascunho) =>
    localStorageAutosaveAdapter.salvar(userId, rascunho),
  limpar: async (userId) => localStorageAutosaveAdapter.limpar(userId),
};

/**
 * Stub da fonte servidor. Hoje devolve sempre `null`, então o fluxo atual
 * (só local) fica intocado.
 *
 * PB-03 — trocar por GET /calculations/draft?type=correction_complete;
 * ver 07-contrato-v2.md §11.1.
 */
export const fonteServidorStub: FonteRascunho = {
  origem: 'servidor',
  carregar: async () => null,
  // no-op até PB-03 existir no back.
  salvar: async () => false,
  limpar: async () => undefined,
};

/** `salvoEm` inválido/ausente conta como o mais antigo possível. */
const instante = (r: RascunhoCalculo): number => {
  const t = Date.parse(r.salvoEm);
  return Number.isFinite(t) ? t : -Infinity;
};

/**
 * Função pura de precedência: só um lado → ele; ambos → `salvoEm` maior;
 * empate (inclusive dois `salvoEm` inválidos) → servidor.
 */
export const escolherRascunho = (
  local: RascunhoCalculo | null,
  servidor: RascunhoCalculo | null,
): { rascunho: RascunhoCalculo; origem: OrigemRascunho } | null => {
  if (!local && !servidor) return null;
  if (!servidor) return { rascunho: local as RascunhoCalculo, origem: 'local' };
  if (!local) return { rascunho: servidor, origem: 'servidor' };
  return instante(local) > instante(servidor)
    ? { rascunho: local, origem: 'local' }
    : { rascunho: servidor, origem: 'servidor' };
};

export interface FontesRascunho {
  local: FonteRascunho;
  servidor: FonteRascunho;
}

export const FONTES_PADRAO: FontesRascunho = {
  local: fonteLocal,
  servidor: fonteServidorStub,
};

/** Uma fonte que rejeita vira `null` e não derruba a outra. */
const carregarTolerante = async (
  fonte: FonteRascunho,
  userId: string | number,
): Promise<RascunhoCalculo | null> => {
  try {
    return await fonte.carregar(userId);
  } catch {
    // Rede/parse/permissão — sem rascunho desta fonte; a outra continua valendo.
    return null;
  }
};

/** Carrega as duas fontes e aplica `escolherRascunho`. */
export const carregarRascunhoComOrigem = async (
  userId: string | number,
  fontes: FontesRascunho = FONTES_PADRAO,
): Promise<{ rascunho: RascunhoCalculo; origem: OrigemRascunho } | null> => {
  const local = await carregarTolerante(fontes.local, userId);
  const servidor = await carregarTolerante(fontes.servidor, userId);
  return escolherRascunho(local, servidor);
};
